import React, { useState } from "react";
import PreviewStep from "../PreviewStep";
import {
  Survey,
  Header,
  Demographics,
  Question,
  Metadata,
} from "../survey-types";
import { HeaderForm } from "./HeaderForm";
import DemographicForm from "./DemographicForm";
import QuestionnaireEditor from "./QuestionnaireEditor";

interface SurveyEditorProps {
  onSubmit: (survey: Survey) => void;
  survey?: Survey;
}

const SurveyEditor: React.FC<SurveyEditorProps> = ({
  onSubmit,
  survey: existingSurvey,
}) => {
  const initialSurvey = existingSurvey
    ? { ...existingSurvey }
    : createEmptySurvey();
  if (!initialSurvey.id) {
    // This could happen when using a template to bootstrap a new survey
    initialSurvey.id = generateUniqueID();
  }
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [survey, setSurvey] = useState<Survey>(initialSurvey);

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleHeaderChange = (key: keyof Header, value: string | Date) => {
    setSurvey({ ...survey, header: { ...survey.header, [key]: value } });
  };

  const setQuestions = (questions: Question[]) => {
    setSurvey({ ...survey, questions });
  };

  const handleDemographicTargetChange = (
    key: keyof Demographics,
    value: any
  ) => {
    setSurvey({
      ...survey,
      demographicTarget: { ...survey.demographicTarget, [key]: value },
    });
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="mx-auto max-w-[800px] sm:mt-8">
            <HeaderForm
              header={survey.header}
              onHeaderChange={handleHeaderChange}
            />
            <DemographicForm
              demographicTarget={survey.demographicTarget}
              onDemographicTargetChange={handleDemographicTargetChange}
            />
          </div>
        );
      case 1:
        return (
          <QuestionnaireEditor
            questions={survey.questions}
            setQuestions={setQuestions}
          />
        );
      case 2:
        return (
          <DemographicForm
            demographicTarget={survey.demographicTarget}
            onDemographicTargetChange={handleDemographicTargetChange}
          />
        );
      case 3:
        return <PreviewStep survey={survey} />;
      default:
        return null;
    }
  };

  return (
    <div className="">
      {renderStep()}

      <div className="mt-8 flex justify-end">
        {currentStep > 0 && (
          <button className="w-32 btn-primary" onClick={handlePrevStep}>
            Previous
          </button>
        )}
        {currentStep < 1 && (
          <button className="w-32 btn-primary" onClick={handleNextStep}>
            Next
          </button>
        )}
        {currentStep === 1 && (
          <button className="w-32 btn-primary" onClick={() => onSubmit(survey)}>
            Submit
          </button>
        )}
      </div>
    </div>
  );
};

function createEmptySurvey(): Survey {
  return {
    id: "survey_" + generateUniqueID(),
    header: getDefaultHeader(),
    metadata: getDefaultMetadata(),
    demographicTarget: getDefaultDemographicTarget(),
    questions: getDefaultQuestions(),
  };
}

function generateUniqueID() {
  return Math.random().toString(36).substring(2, 9);
}

function getDefaultHeader(): Header {
  return {
    title: "",
    contextAndObjectives: "",
  };
}

function getDefaultMetadata(): Metadata {
  return {
    createdBy: "",
    creationDate: new Date().toISOString(),
    language: "en",
  };
}

function getDefaultQuestions(): Question[] {
  return [
    {
      id: generateUniqueID(),
      text: "",
      type: "multiple-choice",
      choices: [
        {
          text: "",
        },
        {
          text: "",
        },
        {
          text: "",
        },
      ],
      required: false,
    },
  ];
}

function getDefaultDemographicTarget(): Demographics {
  return {
    targetAudience: "",
  };
}

export default SurveyEditor;
